import { FIELD_DEFINITIONS } from '../config/fieldDefinitions.jsx';

/**
 * Get searchable fields configuration
 */
export const getSearchableFields = () => {
  return FIELD_DEFINITIONS.filter(field => field.searchable === true);
};

/**
 * Get fields that should appear in search suggestions
 */
export const getSuggestionFields = () => {
  return FIELD_DEFINITIONS.filter(field => field.inSuggestions === true);
};

/**
 * Extract search value from a row for a given field
 */
export const extractSearchValue = (row, field) => {
  // Use field's searchValueGetter if defined
  if (field.searchValueGetter) {
    return field.searchValueGetter(row);
  }
  
  // Direct field access using gridField
  const directValue = row[field.gridField];
  
  
  if (directValue !== undefined && directValue !== null) {
    return String(directValue);
  }
  
  // Use valueGetter if available (pass proper parameters)
  if (field.valueGetter) {
    const value = field.valueGetter(null, row, {});
    return String(value || '');
  }
  
  return '';
};

/**
 * Extract suggestion value from a row for a given field
 */
export const extractSuggestionValue = (row, field) => {
  // Use suggestionValueGetter if defined
  if (field.suggestionValueGetter) {
    return field.suggestionValueGetter(row);
  }
  
  // For source_path, extract filename only
  if (field.id === 'source_path') {
    const path = row?.source_path || row?.filename || '';
    return path.split('/').pop();
  }
  
  // Otherwise use searchValueGetter or standard extraction
  return extractSearchValue(row, field);
};

/**
 * Normalize date search term to multiple possible formats
 */
const normalizeDateSearchTerm = (searchTerm) => {
  // Remove any non-digit characters for analysis
  const digitsOnly = searchTerm.replace(/\D/g, '');

  const variations = [searchTerm]; // Always include original

  // Try to parse as different date formats
  // Format: YYMMDD (6 digits)
  if (digitsOnly.length === 6) {
    const yy = digitsOnly.substr(0, 2);
    const mm = digitsOnly.substr(2, 2);
    const dd = digitsOnly.substr(4, 2);
    variations.push(
      `${yy}/${mm}/${dd}`,
      `${yy}${mm}${dd}`,
      `20${yy}-${mm}-${dd}`,
      `${mm}/${dd}/${yy}`
    );
  }

  // Format: YY/MM/DD or YY-MM-DD with separators
  const datePattern = /^(\d{2})[\/\-](\d{2})[\/\-](\d{2})$/;
  const match = searchTerm.match(datePattern);
  if (match) {
    const [, yy, mm, dd] = match;
    variations.push(
      `${yy}/${mm}/${dd}`,
      `${yy}${mm}${dd}`,
      `20${yy}-${mm}-${dd}`,
      `${mm}/${dd}/${yy}`
    );
  }

  // Partial date patterns (e.g., "09/04" for any year)
  const partialPattern = /^(\d{2})[\/\-](\d{2})$/;
  const partialMatch = searchTerm.match(partialPattern);
  if (partialMatch) {
    const [, part1, part2] = partialMatch;
    // Could be MM/DD or YY/MM
    variations.push(
      `/${part1}/${part2}`, // Matches any YY/MM/DD
      `${part1}/${part2}/`, // Matches any MM/DD/YY
      `-${part1}-${part2}` // Matches any YYYY-MM-DD
    );
  }

  return [...new Set(variations)]; // Remove duplicates
};

/**
 * Check if a row matches search terms
 */
export const rowMatchesSearch = (row, searchWords) => {
  const searchableFields = getSearchableFields();

  // Collect all searchable text from the row
  const searchableTexts = searchableFields.map(field =>
    extractSearchValue(row, field).toLowerCase()
  );

  const allText = searchableTexts.join(' ');

  // Check if all search words are found
  return searchWords.every(word => {
    // Check if this might be a date search term
    if (/^\d{2}[\d\/\-]*\d{0,4}$/.test(word)) {
      // Get normalized date variations
      const dateVariations = normalizeDateSearchTerm(word);
      // Check if any variation matches
      return dateVariations.some(variation =>
        allText.includes(variation.toLowerCase())
      );
    }
    // Regular text search
    return allText.includes(word);
  });
};

/**
 * Generate search suggestions from data
 */
export const generateSearchSuggestions = (data) => {
  const suggestions = new Set();
  const suggestionFields = getSuggestionFields();
  
  data.forEach(row => {
    suggestionFields.forEach(field => {
      const value = extractSuggestionValue(row, field);
      if (value && value !== '' && value !== '<none>') {
        suggestions.add(value);
      }
    });
  });
  
  // Convert to array and sort
  return Array.from(suggestions).sort((a, b) => {
    // Sort numbers first, then strings
    const aIsNum = /^\d+$/.test(a);
    const bIsNum = /^\d+$/.test(b);
    if (aIsNum && !bIsNum) return -1;
    if (!aIsNum && bIsNum) return 1;
    if (aIsNum && bIsNum) return parseInt(a) - parseInt(b);
    return a.localeCompare(b);
  });
};